import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Lectura de la cédula colombiana con pistola de código de barras (PDF417).
 *
 * Soporta las dos formas en que llegan los datos:
 *  - HID / teclado: la pistola (USB o Bluetooth) "teclea" el contenido en el input
 *    enfocado. Se detecta por la ráfaga de teclas y se procesa tras una breve pausa.
 *  - Web Serial: la pistola expone un puerto COM (o USB-CDC). Entrega los bytes crudos.
 *
 * Este módulo NO importa nada del proyecto (solo React) para evitar ciclos de imports.
 */

/** True si la cadena trae bytes de control (señal inequívoca de un volcado PDF417). */
const hasControlChars = (s: string): boolean => {
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    // Se ignoran tab (9), LF (10) y CR (13): pueden aparecer en texto normal.
    if (c < 32 && c !== 9 && c !== 10 && c !== 13) return true;
  }
  return false;
};

/**
 * Extrae el número de cédula del volcado crudo del PDF417.
 *
 * Si el texto NO parece un escaneo (digitación manual: número o texto corto, sin bytes
 * de control), se devuelve INTACTO. Así la búsqueda manual nunca se altera.
 */
export const parseCedulaFromBarcode = (raw: string): string => {
  if (!raw) return '';

  const looksLikeScan = hasControlChars(raw) || raw.length > 40;
  if (!looksLikeScan) return raw; // Digitación manual: no se toca.

  // Ancla: 6-10 dígitos seguidos de la primera letra del apellido, no precedidos por
  // otro dígito. Es el punto más confiable del formato de la Registraduría.
  const anchored = raw.match(/(?<!\d)(\d{6,10})(?=[A-ZÑÁÉÍÓÚ])/);
  if (anchored) {
    // En BD el documento se guarda sin ceros a la izquierda.
    const cedula = anchored[1].replace(/^0+/, '');
    if (cedula.length >= 5) return cedula;
  }

  // Fallback: pistolas que solo envían el número, no el PDF417 completo.
  const digitsOnly = raw.replace(/\D/g, '');
  if (digitsOnly.length >= 5 && digitsOnly.length <= 11) {
    return digitsOnly.replace(/^0+/, '');
  }

  return '';
};

interface UseCedulaScannerOptions {
  /** Lee el valor actual del input (desde el DOM, para evitar estado obsoleto). */
  getInputValue: () => string;
  /** Se invoca con la cédula ya extraída y lista para buscar. */
  onCedula: (cedula: string) => void;
  /** Velocidad del puerto COM. La mayoría de pistolas seriales usan 9600. */
  baudRate?: number;
}

export const useCedulaScanner = ({ getInputValue, onCedula, baudRate = 9600 }: UseCedulaScannerOptions) => {
  const [isScanning, setIsScanning] = useState(false);
  const [serialConnected, setSerialConnected] = useState(false);
  const [serialConnecting, setSerialConnecting] = useState(false);

  const lastKeyTimeRef = useRef(0);
  const scanTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const portRef = useRef<any>(null);
  const readerRef = useRef<any>(null);
  const bufferRef = useRef('');
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Los callbacks se guardan en refs y se refrescan en cada render. Así los handlers
  // que exponemos mantienen una identidad ESTABLE, y no disparan actualizaciones de
  // opciones en los componentes de DevExtreme en cada render.
  const getInputValueRef = useRef(getInputValue);
  const onCedulaRef = useRef(onCedula);
  useEffect(() => {
    getInputValueRef.current = getInputValue;
    onCedulaRef.current = onCedula;
  });

  const serialSupported = typeof navigator !== 'undefined' && 'serial' in navigator;

  /**
   * Detecta la pistola en modo teclado: ráfaga de teclas a < 50 ms.
   * Procesa 300 ms después del último carácter (no requiere Enter).
   * La digitación humana nunca alcanza esa velocidad, así que no interfiere.
   */
  const handleKeyDown = useCallback((e: any) => {
    const native: KeyboardEvent | undefined = e?.event ?? e;
    if (!native || !native.key) return;

    const now = Date.now();
    const elapsed = now - lastKeyTimeRef.current;
    lastKeyTimeRef.current = now;

    if (native.key.length === 1 && elapsed > 0 && elapsed < 50) {
      setIsScanning(true);
      if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
      scanTimerRef.current = setTimeout(() => {
        setIsScanning(false);
        const raw = getInputValueRef.current();
        if (!raw) return;
        const cedula = parseCedulaFromBarcode(raw);
        if (cedula && cedula.length >= 5) onCedulaRef.current(cedula);
      }, 300);
    }
  }, []);

  const handleSerialChunk = useCallback(() => {
    const line = bufferRef.current.trim();
    bufferRef.current = '';
    if (!line) return;
    const cedula = parseCedulaFromBarcode(line);
    if (cedula && cedula.length >= 5) {
      setIsScanning(true);
      setTimeout(() => setIsScanning(false), 600);
      onCedulaRef.current(cedula);
    }
  }, []);

  const connectSerial = useCallback(async () => {
    if (!serialSupported) return;
    try {
      setSerialConnecting(true);
      const port = await (navigator as any).serial.requestPort();
      await port.open({ baudRate });
      portRef.current = port;
      setSerialConnected(true);

      // windows-1252 mapea cada byte 1:1. Con UTF-8 los bytes binarios del PDF417 se
      // volverían U+FFFD y desplazarían las posiciones del contenido.
      const decoder = new (window as any).TextDecoderStream('windows-1252');
      port.readable.pipeTo(decoder.writable).catch(() => {});
      const reader = decoder.readable.getReader();
      readerRef.current = reader;

      (async () => {
        try {
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            bufferRef.current += value;
            // No se parte por \r o \n: el volcado binario puede contenerlos. Se procesa
            // la ráfaga completa tras una breve inactividad.
            if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
            flushTimerRef.current = setTimeout(handleSerialChunk, 150);
          }
        } catch {
          // Puerto cerrado durante la lectura.
        } finally {
          setSerialConnected(false);
        }
      })();
    } catch {
      // El usuario canceló el selector de puerto.
    } finally {
      setSerialConnecting(false);
    }
  }, [baudRate, serialSupported, handleSerialChunk]);

  const disconnectSerial = useCallback(async () => {
    try {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      bufferRef.current = '';
      if (readerRef.current) {
        await readerRef.current.cancel();
        readerRef.current = null;
      }
      if (portRef.current) {
        await portRef.current.close();
        portRef.current = null;
      }
    } catch {
      // Se ignoran errores al cerrar.
    } finally {
      setSerialConnected(false);
    }
  }, []);

  // Limpieza al desmontar.
  useEffect(() => {
    return () => {
      if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      if (readerRef.current) readerRef.current.cancel().catch(() => {});
      if (portRef.current) portRef.current.close().catch(() => {});
    };
  }, []);

  return {
    isScanning,
    handleKeyDown,
    serialSupported,
    serialConnected,
    serialConnecting,
    connectSerial,
    disconnectSerial,
  };
};

export default useCedulaScanner;
